/**
 * Card #281: Reports -> Sales overview. The server now picks each sales-order row by the day
 * the quote became a sales order (`sales_order_date`, its quote_converted_date) instead of
 * created_at OR updated_at. This adds that day as `plot_date`, which the Sales overview charts
 * read (comparisonPlotDate) before falling back to updated_at. Nothing existing is changed:
 * updated_at, created_at and every other field stay exactly as the server sent them, and rows
 * without a sale date (not a sales order, or an older order with no recorded conversion) get
 * no plot_date.
 */
export function withPlotDate<T>(res: T): T {
  const add = (rows: any[]) =>
    rows.map((r: any) =>
      r?.sales_order_date && String(r?.sales_id_num ?? '').trim() !== '' ? { ...r, plot_date: r.sales_order_date } : r,
    );
  if (Array.isArray(res)) return add(res) as any;
  const data = (res as any)?.data;
  if (Array.isArray(data)) return { ...(res as any), data: add(data) };
  return res;
}

/**
 * Card #354: the rows of a reporting/salesOverView answer, whatever shape it arrived in.
 *
 * The answer is normally `{ data: [rows] }` (or a bare array). On dev it sometimes comes back
 * wrapped twice, `{ data: { data: [rows] } }`: the quotes reporting service keeps one shared
 * `this.request`, and another report finishing at the same moment (Sales forecast, the
 * filter reads) deletes `requestType` from the Sales overview request still in flight, so that
 * answer is wrapped by its inner read AND again on the way out. The page only looked at `res`
 * and `res.data`, found no array, and showed "No data available for selected filters" over the
 * rows the server had sent (Weekly, Claude Test and Dashboard, 5 Oct 2026). Its chart totals
 * already read `res.data.data`; the empty check and the rows handed to the widget did not.
 *
 * Every shape gets the sale day (withPlotDate gives the same result when applied twice). The
 * result is always a new array, so sorting or splicing it never reorders the answer; rows without
 * a sale day are the answer's own objects, so tag copies ({ ...row }), as the comparison loads do.
 */
export function salesOverviewRows(res: any): any[] {
  const rows = Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : Array.isArray(res?.data?.data) ? res.data.data
    : [];
  return withPlotDate(rows);
}
